import { createEffect, createEvent, createStore, restore, sample } from 'effector'
import { fx } from '../../../libs'
import { Verification, VerificationApprove, Error } from './types'
import { $params } from '../../../features/router'
import { getVerificationRequests } from '../atoms/duplicates/model'
import {
  setRequestError,
  setRequestSuccess,
} from '../../../features/notifications'

export const verificationRequestMountedV2 = createEvent()
export const setVerificationRequestV2 = createEvent<Verification>()
export const submitApproveV2 = createEvent<VerificationApprove>()
export const submitBackToNewV2 = createEvent<Verification>()
export const submitEditVerificationRequestV2 = createEvent<Verification>()
export const submitSaveAndApproveV2 = createEvent<Verification>()
export const setErrorV2 = createEvent<Error>()
export const setIsFormDirtyV2 = createEvent<boolean>()
export const $isFormDirtyV2 = restore(setIsFormDirtyV2, false)

export type FormDirtyStateV2 = {
  dirtyFieldKeys: string[]
  expirationDate: string
  values: Pick<
    Verification,
    | 'firstName'
    | 'middleName'
    | 'lastName'
    | 'country'
    | 'dateOfBirth'
    | 'documentNumber'
    | 'expirationDate'
  >
}
export const setFormDirtyStateV2 = createEvent<FormDirtyStateV2>()
export const $formDirtyStateV2 = restore<FormDirtyStateV2>(
  setFormDirtyStateV2,
  {
    dirtyFieldKeys: [],
    expirationDate: '',
    values: {
      firstName: '', middleName: '', lastName: '', country: '', dateOfBirth: '', documentNumber: '', expirationDate: '',
    },
  },
)
export const $verificationRequestV2 = restore(setVerificationRequestV2, {
  requestId: '',
  clientId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  status: '',
  country: '',
  city: '',
  address: '',
  zip: '',
  documentType: '',
  documentNumber: '',
  fileNames: [],
  browserFileNames: [],
  exampleFiles: '',
  expirationDate: '',
  isDuplicate: false,
  profileNotes: '',
  comment: '',
  reason: '',
  manager: {
    id: '',
    user: {
      email: '',
      fullname: '',
    },
  },
  client: {
    clientId: '',
    email: '',
    fullName: '',
    partner: false,
    verified: false,
    hasDeposit: false,
    isPremium: false,
    isTest: false,
  },
  totalBalanceUSD: 0,
  systemComment: null,
  requiredPreprocessedSteps: [],
})

export const $errorV2 = restore(setErrorV2, {
  message: '',
  code: 0,
})
export const getVerificationV2 = fx<void, Verification>({
  method: 'GET',
  url: `/verification/`,
})

sample({
  clock: verificationRequestMountedV2,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.verificationID
    }
    return {
      url: `/verification/${id}`,
    }
  },
  target: getVerificationV2,
})

type VerificationStatusCommandBody = Record<string, never>
type VerificationDataUpdateBody = Omit<
  Verification,
  'status' | 'reason' | 'comment' | 'requestId' | 'clientId' | 'manager' | 'client'
>

export const approveVerificationV2 = fx<VerificationStatusCommandBody, Verification>({
  method: 'POST',
  url: `/verification/`,
})

sample({
  clock: submitApproveV2,
  fn: (verification) => {
    return {
      body: {},
      url: `/verification/${verification.requestId}/accept`,
    }
  },
  target: approveVerificationV2,
})

export const backToNewVerificationV2 = fx<VerificationStatusCommandBody, Verification>({
  method: 'POST',
  url: '/verification/',
})

sample({
  clock: submitBackToNewV2,
  fn: (verification) => ({
    body: {},
    url: `/verification/${verification.requestId}/rollback`,
  }),
  target: backToNewVerificationV2,
})

sample({
  clock: backToNewVerificationV2.doneData,
  fn: (doneData) => doneData.body,
  target: setVerificationRequestV2,
})

sample({
  clock: backToNewVerificationV2.doneData,
  fn: () => true,
  target: setRequestSuccess,
})

sample({
  clock: backToNewVerificationV2.doneData,
  target: verificationRequestMountedV2,
})

sample({
  clock: backToNewVerificationV2.failData,
  fn: (failData) => ({
    message: failData.body.message,
    code: failData.body.code,
  }),
  target: setRequestError,
})

export const updateVerificationRequestV2 = fx<VerificationDataUpdateBody, Verification>({
  method: 'PUT',
  url: '/verification/',
})

const $approveAfterSave = createStore(false)
  .on(submitSaveAndApproveV2, () => true)
  .reset([submitApproveV2, updateVerificationRequestV2.failData])

const toDataUpdateRequest = (verification: Verification) => ({
  url: `/verification/${verification.requestId}`,
  body: (({ status, reason, comment, requestId, clientId, manager, client, ...fields }) => fields)(verification),
})

sample({
  clock: submitEditVerificationRequestV2,
  source: $params,
  fn: (_, verification) => toDataUpdateRequest(verification),
  target: updateVerificationRequestV2,
})

sample({
  clock: submitSaveAndApproveV2,
  fn: toDataUpdateRequest,
  target: updateVerificationRequestV2,
})

sample({
  clock: updateVerificationRequestV2.doneData,
  source: $approveAfterSave,
  filter: Boolean,
  fn: (_, doneData) => doneData.body,
  target: submitApproveV2,
})

sample({
  clock: getVerificationV2.doneData,
  fn: (doneData) => doneData.body,
  target: setVerificationRequestV2,
})

sample({
  clock: approveVerificationV2.doneData,
  fn: (doneData) => doneData.body,
  target: setVerificationRequestV2,
})

sample({
  clock: [updateVerificationRequestV2.doneData],
  fn: (doneData) => {
    return {
      url: `/verification/${doneData?.body?.requestId}/duplicates`,
    }
  },
  target: [getVerificationRequests],
})

sample({
  clock: updateVerificationRequestV2.doneData,
  fn: (doneData) => doneData.body,
  target: setVerificationRequestV2,
})

sample({
  clock: updateVerificationRequestV2.failData,
  filter: (failData) => failData.body.code === 409,
  target: verificationRequestMountedV2,
})

sample({
  clock: updateVerificationRequestV2.failData,
  source: $verificationRequestV2,
  filter: (_, failData) => failData.body.code === 409,
  fn: (verification) => ({
    url: `/verification/${verification.requestId}/duplicates`,
  }),
  target: getVerificationRequests,
})

sample({
  clock: approveVerificationV2.doneData,
  fn: () => true,
  target: setRequestSuccess,
})

sample({
  clock: approveVerificationV2.failData,
  fn: (failData) => ({
    message: failData.body.message,
    code: failData.body.code,
  }),
  target: setRequestError,
})

sample({
  clock: updateVerificationRequestV2.doneData,
  fn: () => true,
  target: setRequestSuccess,
})

sample({
  clock: [updateVerificationRequestV2.failData],
  filter: (failData) => failData.body.code !== 409,
  fn: (failData) => {
    return { message: failData.body.message, code: failData.body.code }
  },
  target: [setErrorV2],
})

sample({
  clock: updateVerificationRequestV2.failData,
  filter: (failData) => failData.body.code !== 409,
  fn: (failData) => ({
    message: failData.body.message,
    code: failData.body.code,
  }),
  target: setRequestError,
})
